/**
 * v96.1.0 · Restablecer contraseña con CÓDIGO de verificación (reemplaza el enlace por correo).
 * Paso 1: correo → se envía un código de 6 dígitos (15 min).
 * Paso 2: ingresar el código.
 * Paso 3: nueva contraseña.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowRight, ArrowLeft, Loader2, CheckCircle2, KeyRound, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// v101.78.0 · Código pendiente (mismo navegador): evita el bucle «abrir el enlace del correo → paso 1 → pedir otro
//            código → el anterior deja de servir». El enlace del correo abre directo el paso del código.
const PENDING_KEY = "afc_reset_pending";
const PENDING_MIN = 15;
const readPending = () => {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    if (p && p.email && Date.now() - (p.ts || 0) < PENDING_MIN * 60 * 1000) return p;
  } catch (_) { /* noop */ }
  return null;
};
const savePending = (email) => { try { localStorage.setItem(PENDING_KEY, JSON.stringify({ email, ts: Date.now() })); } catch (_) { /* noop */ } };
const clearPending = () => { try { localStorage.removeItem(PENDING_KEY); } catch (_) { /* noop */ } };
const fromEmailLink = () => {
  try { return new URLSearchParams(window.location.search).get("paso") === "codigo"; } catch (_) { return false; }
};

const errMsg = (err, fb) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (Array.isArray(d?.errors)) return d.errors.join(" · ");
  return fb;
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const _pending = readPending();
  const [step, setStep] = useState(fromEmailLink() ? 2 : 1);
  const [email, setEmail] = useState(_pending?.email || "");
  const [pendingInfo, setPendingInfo] = useState(_pending);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // Limpiar sesión vieja para que no interfiera con el flujo público
  useEffect(() => {
    localStorage.removeItem("afc_token");
    localStorage.removeItem("afc_refresh");
    localStorage.removeItem("afc_user");
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      await api.post("/v1/auth/forgot-password", { email });
      toast.success("Listo. Revisa tu correo: te llegará un código de 6 dígitos en unos minutos. Escríbelo en esta misma pantalla.", { duration: 8000 });
      savePending(email.trim().toLowerCase()); setPendingInfo(readPending());
      setStep(2); setCode(""); setResendIn(60);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Pediste demasiados códigos seguidos. Espera 1 minuto y vuelve a intentarlo.");
      } else {
        toast.error(errMsg(err, "No pudimos enviar el código. Revisa tu conexión e inténtalo de nuevo."));
      }
    } finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || loading) return;
    if (!email) { toast.error("Escribe el correo al que te llegó el código."); return; }
    setLoading(true);
    try {
      await api.post("/v1/auth/reset-password/verify-code", { email, code });
      setStep(3);
    } catch (err) {
      toast.error(errMsg(err, "El código no es válido. Usa el código del ÚLTIMO correo que recibiste."), { duration: 8000 });
    } finally { setLoading(false); }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (pw !== pw2) { toast.error("Las dos contraseñas no son iguales. Escríbelas de nuevo."); return; }
    setLoading(true);
    try {
      await api.post("/v1/auth/reset-password/code", { email, code, new_password: pw });
      clearPending();
      setStep(4);
    } catch (err) {
      const m = errMsg(err, "No se pudo cambiar la contraseña");
      toast.error(m);
      if (/c[oó]digo|intentos|venci/i.test(m)) setStep(2);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full pl-10 pr-3 py-2.5 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E0E621] focus:border-transparent transition";
  const btnCls = "w-full px-4 py-2.5 bg-[#0A3747] hover:bg-[#135166] text-white text-sm font-semibold rounded transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed";
  const labelCls = "block text-[11px] font-medium text-gray-700 mb-1.5 uppercase tracking-wider font-mono";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="bg-[#0A3747] px-6 py-5 border-b-4 border-[#E0E621]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#E0E621]/20 flex items-center justify-center">
              {step === 1 ? <Mail className="text-[#E0E621]" size={20} /> : step === 2 ? <KeyRound className="text-[#E0E621]" size={20} /> : <Lock className="text-[#E0E621]" size={20} />}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E0E621] font-mono">Alfa-Field Connect</div>
              <h1 className="text-lg font-bold text-white" style={{ fontFamily: "Space Grotesk" }}>Restablecer contraseña</h1>
            </div>
          </div>
          {step <= 3 && <div className="text-[10px] text-white/70 mt-2 font-mono">Paso {step} de 3</div>}
        </div>

        {step === 1 && (
          <form onSubmit={sendCode} className="p-6 space-y-4">
            <p className="text-sm text-gray-600 leading-relaxed">
              Escribe tu correo corporativo y pulsa <strong>«Enviar código»</strong>. Te llegará un correo con un
              <strong> código de 6 dígitos</strong> que debes escribir <strong>en esta misma pantalla</strong>.
            </p>
            {pendingInfo && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="reset-pending-notice">
                <strong>Ya te enviamos un código</strong> a {pendingInfo.email}. Búscalo en tu correo y escríbelo aquí.
                No pidas otro: cada código nuevo anula el anterior.
                <button type="button" onClick={() => { setEmail(pendingInfo.email); setStep(2); }}
                  className="mt-2 w-full px-3 py-2 rounded bg-[#0A3747] text-white font-semibold" data-testid="reset-have-code">
                  Ya tengo el código, quiero escribirlo
                </button>
              </div>
            )}
            <div>
              <label className={labelCls}>Email corporativo</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls}
                  placeholder="usuario@telealfacom.com" required autoComplete="email" autoFocus data-testid="forgot-email-input" />
              </div>
            </div>
            <button type="submit" disabled={loading || !email} className={btnCls} data-testid="submit-forgot-password">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Enviar código <ArrowRight size={14} /></>}
            </button>
            <button type="button" onClick={() => navigate("/login")} className="w-full flex items-center justify-center gap-1.5 text-xs text-gray-600 hover:text-[#0A3747] transition py-1" data-testid="cancel-forgot-btn">
              <ArrowLeft size={12} /> Volver al inicio de sesión
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyCode} className="p-6 space-y-4" data-testid="forgot-step-code">
            {email ? (
              <p className="text-sm text-gray-600 leading-relaxed">
                Enviamos un código de 6 dígitos a <strong>{email}</strong>. Escríbelo abajo y pulsa <strong>«Verificar código»</strong>.
                <br />· Sirve por <strong>15 minutos</strong>. Si no lo ves, revisa Spam o Promociones.
                <br />· Si recibiste varios correos, usa el código del <strong>más reciente</strong>.
                <br />· <strong>No pidas otro código</strong> mientras esperas: cada código nuevo anula el anterior.
              </p>
            ) : (
              <div>
                <p className="text-sm text-gray-600 leading-relaxed mb-2">
                  Escribe tu correo corporativo y el código de 6 dígitos del <strong>último</strong> correo que recibiste.
                </p>
                <label className={labelCls}>Email corporativo</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls}
                    placeholder="usuario@telealfacom.com" autoComplete="email" data-testid="reset-code-email-input" />
                </div>
              </div>
            )}
            <div>
              <label className={labelCls}>Código de verificación</label>
              <input inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md text-center text-2xl font-mono tracking-[0.6em] focus:outline-none focus:ring-2 focus:ring-[#E0E621]"
                placeholder="••••••" data-testid="reset-code-input" />
            </div>
            <button type="submit" disabled={loading || code.length !== 6} className={btnCls} data-testid="submit-reset-code">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Verificar código <ArrowRight size={14} /></>}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => setStep(1)} className="text-gray-600 hover:text-[#0A3747] flex items-center gap-1"><ArrowLeft size={12} /> Me equivoqué de correo</button>
              <button type="button" onClick={sendCode} disabled={resendIn > 0 || loading} className="text-[#0A3747] font-semibold disabled:text-gray-400" data-testid="resend-code">
                {resendIn > 0 ? `Pedir otro código (${resendIn}s)` : "No me llegó: pedir otro código"}
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={resetPw} className="p-6 space-y-4" data-testid="forgot-step-password">
            <p className="text-sm text-gray-600">Código correcto. Ahora escribe tu nueva contraseña dos veces.</p>
            {[["Nueva contraseña", pw, setPw, "new-password-input"], ["Confirmar contraseña", pw2, setPw2, "confirm-password-input"]].map(([lbl, val, setVal, tid]) => (
              <div key={tid}>
                <label className={labelCls}>{lbl}</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type={showPw ? "text" : "password"} value={val} onChange={(e) => setVal(e.target.value)}
                    className={inputCls} autoComplete="new-password" required data-testid={tid} />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
            ))}
            <div className="text-[11px] text-gray-500">Debe tener mayúscula, minúscula, número y un símbolo (!@#$%…), y al menos 12 caracteres. No puede repetir contraseñas recientes.</div>
            <button type="submit" disabled={loading || !pw || !pw2} className={btnCls} data-testid="submit-new-password">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Cambiar contraseña <ArrowRight size={14} /></>}
            </button>
          </form>
        )}

        {step === 4 && (
          <div className="p-6 space-y-4" data-testid="forgot-password-success">
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="text-emerald-600" size={28} />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Contraseña actualizada</h2>
              <p className="text-sm text-gray-600 mt-2">Tu contraseña se cambió. Pulsa el botón y entra con tu correo y la nueva contraseña.</p>
            </div>
            <button onClick={() => navigate("/login")} className={btnCls} data-testid="back-to-login-btn">
              <ArrowLeft size={14} /> Ir al inicio de sesión
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
